import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CarServices from '../../services/CarServices';
import CategoryServices from '../../services/CategoryServices';

function AddService() {
    const navigate = useNavigate();

    const [formValues, setFormValues] = useState({
        vendorId: '',
        category: '',
        subCategory: '',
        title: '',
        description: '',
        mobileNo: '',
        sequence:"",
        logo: '',
        images: [],
        address: '',
        state: '',
        cityName: '',
        latitude: '',
        longitude: '',
        openTime: '',
        closeTime: '',
        socialLinks: {
            instagram: '',
            facebook: '',
            website: ''
        }
    });
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [logoPreview, setLogoPreview] = useState('');
    const [uploadingImages, setUploadingImages] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    /* ================= CATEGORY ================= */
    useEffect(() => {
        CategoryServices.getCategoryActive().then(res => {
            if (res.status) setCategories(res.data);
        });
    }, []);

    useEffect(() => {
        CarServices.getVendorActive().then(res => {
            if (res.status) setVendors(res.data);
        });
    }, []);

    useEffect(() => {
        CarServices.getState().then(res => {
            if (res.status) setStates(res.data);
        });
    }, []);

    /* ================= INPUT ================= */
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        if (name === 'category') {
            setFormValues(prev => ({
                ...prev,
                category: value,
                subCategory: ''
            }));
            const res = await CategoryServices.getSubCategoryByCategory(value);
            setSubCategories(res?.data?.[0]?.subcategory || []);
            return;
        }

        if (name === 'state') {
            setFormValues(prev => ({
                ...prev,
                state: value,
                cityName: '' // Reset city when state changes
            }));

            // Fetch cities for selected state
            setLoadingCities(true);
            try {
                const res = await CarServices.getCities(value);
                if (res.status) {
                    setCities(res.data);
                } else {
                    setCities([]);
                }
            } catch (error) {
                console.error('Error fetching cities:', error);
                setCities([]);
            } finally {
                setLoadingCities(false);
            }
            return;
        }

        setFormValues(prev => ({ ...prev, [name]: value }));
    };

    const handleSocialChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({
            ...prev,
            socialLinks: {
                ...prev.socialLinks,
                [name]: value
            }
        }));
    };

    /* ================= LOGO ================= */
    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('images', file);

        try {
            const res = await CarServices.uploadImage(formData);
            if (res.images?.length) {
                setFormValues(prev => ({ ...prev, logo: res.images[0] }));
                setLogoPreview(res.images[0]);
            }
        } catch (error) {
            console.error('Logo upload error:', error);
            alert('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    /* ================= MULTIPLE IMAGES ================= */
    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploadingImages(true);
        const formData = new FormData();
        files.forEach(f => formData.append('images', f));

        try {
            const res = await CarServices.uploadImage(formData);
            if (res.images && res.images.length > 0) {
                setFormValues(prev => ({
                    ...prev,
                    images: [...prev.images, ...res.images]
                }));
                setPreviewImages(prev => [...prev, ...res.images]);
            }
        } catch (error) {
            console.error('Images upload error:', error);
            alert('Failed to upload images');
        } finally {
            setUploadingImages(false);
            e.target.value = '';
        }
    };

    /* ================= REMOVE IMAGE ================= */
    const removeImage = (imageToRemove) => {
        setFormValues(prev => ({
            ...prev,
            images: prev.images.filter(img => img !== imageToRemove)
        }));
        setPreviewImages(prev => prev.filter(img => img !== imageToRemove));
    };

    const removeLogo = () => {
        setFormValues(prev => ({ ...prev, logo: '' }));
        setLogoPreview('');
    };
    const formatTime = (time) => {
        let [h, m] = time.split(':');
        h = parseInt(h);

        let ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;

        return `${h.toString().padStart(2, '0')}.${m} ${ampm}`;
    };
    /* ================= SUBMIT ================= */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formValues.vendorId) {
            alert('Please select a vendor');
            return;
        }
        if (!formValues.category) {
            alert('Please select a category');
            return;
        }
        if (!formValues.subCategory) {
            alert('Please select a sub category');
            return;
        }
        if (!formValues.title) {
            alert('Please enter a title');
            return;
        }
        if (!formValues.mobileNo) {
            alert('Please enter mobile number');
            return;
        }
        if (!formValues.address) {
            alert('Please enter address');
            return;
        }
        if (!formValues.state) {
            alert('Please select a state');
            return;
        }
        if (!formValues.cityName) {
            alert('Please select a city');
            return;
        }
        if (!formValues.openTime) {
            alert('Please select a open time');
            return;
        }
        if (!formValues.closeTime) {
            alert('Please select a close time');
            return;
        }
        if (formValues.openTime >= formValues.closeTime) {
            alert("Close time must be after open time");
            return;
        }
        const payload = {
            ...formValues,
            openTime: formatTime(formValues.openTime),
            closeTime: formatTime(formValues.closeTime)
        };

        try {
            const res = await CarServices.createService(payload);
            if (res.status) {
                alert('Service Created Successfully');
                navigate('/shop-list');
            } else {
                alert(res.message || 'Failed to create service');
            }
        } catch (error) {
            console.error('Submit error:', error);
            alert('Error creating service');
        }
    };

    return (
        <div className="right_col">
            <div className="title-box">
                <h2>Shop Add</h2>
                <Link className="list-banner-btn" to="/shop-list">Shop List</Link>
            </div>

            <div className="container-box px-5">
                <form onSubmit={handleSubmit}>
                    <div className="row">

                        {/* Vendor */}
                        <div className="col-lg-6">
                            <label>Owner</label>
                            <select name="vendorId" className="form-control"
                                value={formValues.vendorId}
                                onChange={handleInputChange} >
                                <option value="">Select Owner</option>
                                {vendors.map(v => (
                                    <option key={v._id} value={v._id}>{v.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Category */}
                        <div className="col-lg-6">
                            <label>Category</label>
                            <select name="category" className="form-control"
                                value={formValues.category}
                                onChange={handleInputChange}>
                                <option value="">Select Category</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>{c.title}</option>
                                ))}
                            </select>
                        </div>

                        {/* SubCategory */}
                        <div className="col-lg-6">
                            <label>Sub Category*</label>
                            <select name="subCategory" className="form-control"
                                value={formValues.subCategory}
                                onChange={handleInputChange} >
                                <option value="">Select Sub Category</option>
                                {subCategories.map(s => (
                                    <option key={s._id} value={s._id}>{s.title}</option>
                                ))}
                            </select>
                        </div>

                        {/* Title */}
                        <div className="col-lg-6 mt-3">
                            <label>Title</label>
                            <input className="form-control" name="title"
                                placeholder="Title"
                                value={formValues.title}
                                onChange={handleInputChange} />
                        </div>

                        {/* Description */}


                        {/* Mobile */}
                        <div className="col-lg-6 mt-3">
                            <label>Mobile No</label>
                            <input className="form-control" name="mobileNo"
                                placeholder="Mobile No"
                                value={formValues.mobileNo}
                                maxLength={10}
                                onChange={handleInputChange} />
                        </div>

                        {/* Logo */}
                        <div className="col-lg-6 mt-3">
                            <label>Logo (Single Image)</label>
                            <input type="file" className="form-control"
                                onChange={handleLogoChange}
                                disabled={uploadingLogo}
                                accept="image/*" />
                            {uploadingLogo && <p className="text-muted mt-1">Uploading...</p>}
                            {logoPreview && (
                                <div className="mt-2 position-relative d-inline-block">
                                    <img
                                        src={`${process.env.REACT_APP_URL}${logoPreview}`}
                                        width="80"
                                        height="80"
                                        style={{ objectFit: 'cover', borderRadius: '4px' }}
                                        alt="Logo preview"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeLogo}
                                        className="btn btn-danger btn-sm"
                                        style={{ position: 'absolute', top: '-8px', right: '-8px', borderRadius: '50%', padding: '2px 6px' }}
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Images */}
                        <div className="col-lg-6 mt-3">
                            <label>Images (Multiple)</label>
                            <input
                                type="file"
                                multiple
                                className="form-control"
                                onChange={handleFileChange}
                                disabled={uploadingImages}
                                accept="image/*"
                            />
                            {uploadingImages && <p className="text-muted mt-1">Uploading images...</p>}

                            {/* Preview Multiple Images */}
                            {previewImages.length > 0 && (
                                <div className="mt-2" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    {previewImages.map((img, index) => (
                                        <div key={index} className="position-relative d-inline-block">
                                            <img
                                                src={`${process.env.REACT_APP_URL}${img}`}
                                                width="80"
                                                height="80"
                                                style={{ objectFit: 'cover', borderRadius: '4px' }}
                                                alt={`Preview ${index + 1}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(img)}
                                                className="btn btn-danger btn-sm"
                                                style={{ position: 'absolute', top: '-8px', right: '-8px', borderRadius: '50%', padding: '2px 6px' }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Address */}
                        <div className="col-lg-6 mt-3">
                            <label>Address</label>
                            <input className="form-control" name="address"
                                placeholder="Address"
                                value={formValues.address}
                                onChange={handleInputChange} />
                        </div>

                        {/* State Dropdown */}
                        <div className="col-lg-6 mt-3">
                            <label>State</label>
                            <select name="state" className="form-control"
                                value={formValues.state}
                                onChange={handleInputChange} >
                                <option value="">Select State</option>
                                {states.map(state => (
                                    <option key={state._id} value={state._id}>{state.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* City Dropdown */}
                        <div className="col-lg-6 mt-3">
                            <label>City</label>
                            <select name="cityName" className="form-control"
                                value={formValues.cityName}
                                onChange={handleInputChange}
                                disabled={!formValues.state || loadingCities}>
                                <option value="">{loadingCities ? "Loading cities..." : "Select City"}</option>
                                {cities.map(city => (
                                    <option key={city._id} value={city._id}>{city.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Latitude */}
                        <div className="col-lg-3 mt-3">
                            <label>Latitude</label>
                            <input type="number" step="any" className="form-control" name="latitude"
                                placeholder="Latitude"
                                value={formValues.latitude}
                                onChange={handleInputChange} />
                        </div>

                        {/* Longitude */}
                        <div className="col-lg-3 mt-3">
                            <label>Longitude</label>
                            <input type="number" step="any" className="form-control" name="longitude"
                                placeholder="Longitude"
                                value={formValues.longitude}
                                onChange={handleInputChange} />
                        </div>
                        {/* Open Time */}
                        <div className="col-lg-3 mt-3">
                            <label>Open Time</label>
                            <input
                                type="time"
                                className="form-control"
                                name="openTime"
                                value={formValues.openTime}
                                onChange={handleInputChange}

                            />
                        </div>

                        {/* Close Time */}
                        <div className="col-lg-3 mt-3">
                            <label>Close Time</label>
                            <input
                                type="time"
                                className="form-control"
                                name="closeTime"
                                value={formValues.closeTime}
                                onChange={handleInputChange}

                            />
                        </div>



                        <div className="col-lg-12 mt-3">
                            <label>Description</label>
                            <textarea className="form-control" name="description"
                                placeholder="Description"
                                rows="4"
                                value={formValues.description}
                                onChange={handleInputChange} />
                        </div>
                        {/* Social Links */}
                        <div className="col-lg-12 mt-3">
                            <label>Social Links</label>
                        </div>
                        <div className="col-lg-4 mt-1">
                            <input className="form-control" name="instagram"
                                placeholder="Instagram URL"
                                value={formValues.socialLinks.instagram}
                                onChange={handleSocialChange} />
                        </div>

                        <div className="col-lg-4 mt-1">
                            <input className="form-control" name="facebook"
                                placeholder="Facebook URL"
                                value={formValues.socialLinks.facebook}
                                onChange={handleSocialChange} />
                        </div>

                        <div className="col-lg-4 mt-1">
                            <input className="form-control" name="website"
                                placeholder="Website URL"
                                value={formValues.socialLinks.website}
                                onChange={handleSocialChange} />
                        </div>
                           <div className="col-lg-6 col-md-6">
                    <div className="input-field">
                      <label>Select Sequence</label>
                      <select className="form-control" value={formValues.sequence} 
                        onChange={handleInputChange} name="sequence">
                      <option value="1">1</option>
    <option value="2">2</option>
    <option value="3">3</option>
    <option value="4">4</option>
    <option value="5">5</option>
    <option value="6">6</option>
    <option value="7">7</option>
    <option value="8">8</option>
    <option value="9">9</option>
    <option value="10">10</option>
    <option value="11">11</option>
    <option value="12">12</option>
    <option value="13">13</option>
    <option value="14">14</option>
    <option value="15">15</option>
    <option value="16">16</option>
    <option value="17">17</option>
    <option value="18">18</option>
    <option value="19">19</option>
    <option value="20">20</option>
    <option value="21">21</option>
    <option value="22">22</option>
    <option value="23">23</option>
    <option value="24">24</option>
    <option value="25">25</option>
    <option value="26">26</option>
    <option value="27">27</option>
    <option value="28">28</option>
    <option value="29">29</option>
    <option value="30">30</option>
    <option value="31">31</option>
    <option value="32">32</option>
    <option value="33">33</option>
    <option value="34">34</option>
    <option value="35">35</option>
    <option value="36">36</option>
    <option value="37">37</option>
    <option value="38">38</option>
    <option value="39">39</option>
    <option value="40">40</option>
                      </select>
                      
                    </div>
                  </div>

                    </div>

                    <button className="submit-green-btn mt-4" type="submit">SUBMIT</button>
                </form>
            </div>
        </div>
    );
}

export default AddService;